import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useTranslation } from '@/i18n'
import { pageWindow } from './sheetPaging'

/**
 * Page navigator for a multi-sheet drawing — a single multi-page PDF
 * (`?pnidPages=1:uuid,2:uuid`) or a grouped multi-file P&ID set.
 *
 * Extracted from the V1 editor header so the V2 chrome renders the same
 * control: FS-654's page navigation existed only inside EditorWrapper's
 * `!isV2` header, so on V2 a 12-sheet PDF opened on page 1 with no way off it.
 * One implementation, two skins — `variant="v1"` keeps the legacy header
 * pixel-identical; `variant="canvas"` is the V2 floating control, which draws
 * on its host card's surface rather than carrying a background of its own.
 */
interface SheetPageBarProps {
  currentIndex: number
  total: number
  /** Sheet names for per-button tooltips; index-aligned with the page order. */
  sheetNames?: string[]
  onNavigate: (index: number) => void
  disabled?: boolean
  variant?: 'v1' | 'canvas'
  /** Page buttons rendered before collapsing the middle into an ellipsis. */
  maxVisible?: number
}

const PAGE_BUTTON_BASE = 'min-w-[28px] h-7 px-2 text-sm font-medium rounded transition-colors disabled:opacity-50'

const SKIN = {
  v1: {
    active: 'bg-indigo-800 text-white',
    idle: 'bg-gray-100 dark:bg-dark-accent text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-dark-muted',
    ellipsis: 'text-sm text-gray-400 dark:text-gray-500 px-0.5',
  },
  canvas: {
    active: 'bg-primary text-primary-foreground',
    idle: 'text-muted-foreground hover:bg-muted hover:text-foreground',
    ellipsis: 'text-sm text-muted-foreground px-0.5',
  },
} as const

export default function SheetPageBar({
  currentIndex,
  total,
  sheetNames,
  onNavigate,
  disabled = false,
  variant = 'canvas',
  maxVisible = 5,
}: SheetPageBarProps) {
  const { t } = useTranslation()
  const skin = SKIN[variant]

  if (total <= 1) return null

  const [windowStart, windowEnd] = pageWindow(currentIndex, total, maxVisible)

  const pageButton = (index: number) => (
    <button
      key={index}
      type="button"
      onClick={() => onNavigate(index)}
      disabled={disabled}
      aria-label={t('data_catalog:graph_editor.header.go_to_sheet', {
        number: index + 1,
        defaultValue: 'Go to sheet {{number}}',
      })}
      aria-current={index === currentIndex ? 'page' : undefined}
      className={`${PAGE_BUTTON_BASE} ${index === currentIndex ? skin.active : skin.idle}`}
      title={sheetNames?.[index]}
    >
      {index + 1}
    </button>
  )

  return (
    <nav
      className="flex items-center gap-2"
      aria-label={t('data_catalog:graph_editor.header.sheet_nav', { defaultValue: 'Sheet navigation' })}
    >
      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate(currentIndex - 1)}
        disabled={currentIndex <= 0 || disabled}
        className="h-7 w-7 p-0"
        aria-label={t('data_catalog:graph_editor.header.previous_sheet', { defaultValue: 'Previous sheet' })}
      >
        {/* Flex order already mirrors under dir=rtl, so the glyph has to turn
            too or "previous" would point away from where it goes. Same
            treatment as components/ui/pagination.tsx. */}
        <ChevronLeft className="w-4 h-4 rtl:rotate-180" />
      </Button>

      <div className="flex gap-1 items-center">
        {/* The first/last page stay reachable in one click when the window has
            scrolled past them — otherwise sheet 1 of 40 needs eight hops.
            The ellipsis is separate: at windowStart === 1 the jump button IS
            page 1 and the window starts at page 2, so a "..." there would
            stand in for nothing. */}
        {windowStart > 0 && (
          <>
            {pageButton(0)}
            {windowStart > 1 && <span className={skin.ellipsis}>...</span>}
          </>
        )}
        {Array.from({ length: windowEnd - windowStart }, (_, i) => pageButton(windowStart + i))}
        {windowEnd < total && (
          <>
            {windowEnd < total - 1 && <span className={skin.ellipsis}>...</span>}
            {pageButton(total - 1)}
          </>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        onClick={() => onNavigate(currentIndex + 1)}
        disabled={currentIndex >= total - 1 || disabled}
        className="h-7 w-7 p-0"
        aria-label={t('data_catalog:graph_editor.header.next_sheet', { defaultValue: 'Next sheet' })}
      >
        <ChevronRight className="w-4 h-4 rtl:rotate-180" />
      </Button>
    </nav>
  )
}
